/**
 * R158x · Feb 2026 — Project status change confirmation dialog.
 *
 * Replaces the raw `window.prompt` used to collect a justification when
 * a PM changes a project's status on the Planning Board. Renders a
 * proper shadcn Dialog with:
 *   - From → To banner
 *   - Preset chips (Client hold, Waiting on assets, Awaiting approval,
 *     Blocked by dependency, Delivered, Other)
 *   - Textarea (auto-focused, required)
 *   - Cancel + Confirm buttons
 *
 * On confirm, calls `onConfirm(reason: string)`. Backend contract
 * unchanged — the reason ends up in activity log metadata via the
 * existing `status_change_reason` field on PATCH /projects/{id}.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight } from "lucide-react";

const PRESETS = {
  hold: ["Client hold — awaiting decision", "Client hold — budget review", "Awaiting client approval"],
  paused: ["Paused — insufficient bandwidth", "Paused — dependency blocked", "Paused — season low"],
  blocked: ["Blocked by client input", "Blocked by asset delivery", "Blocked by upstream dependency"],
  at_risk: ["At risk — running behind schedule", "At risk — scope drift", "At risk — resourcing gap"],
  active: ["Resuming after hold", "Kickoff green-lit", "Client back online"],
  done: ["Delivered + client signed off", "Delivered internally, awaiting client confirmation"],
  planned: ["Backlog — starts next month", "Backlog — awaiting kickoff meeting"],
  archived: ["Cancelled by client", "No further work planned"],
};

export default function ProjectStatusChangeDialog({ open, project, fromStatus, toStatus, statusMeta, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setBusy(false); }
  }, [open]);

  const presets = PRESETS[toStatus] || [];

  const submit = async () => {
    const v = (reason || "").trim();
    if (!v) return;
    setBusy(true);
    try { await onConfirm(v); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent
        className="max-w-lg rounded-none"
        data-testid="project-status-change-dialog"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && reason.trim()) {
            e.preventDefault(); submit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg">
            Change project status
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground -mt-2 mb-2">
          <span className="font-medium text-foreground">{project?.name}</span>
        </div>

        {/* From → To banner */}
        <div className="flex items-center gap-2 border border-border p-2 bg-muted/30" data-testid="status-transition-banner">
          <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${statusMeta?.[fromStatus]?.cls || "border-border"}`}>
            {statusMeta?.[fromStatus]?.label || fromStatus || "—"}
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
          <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${statusMeta?.[toStatus]?.cls || "border-border"}`}>
            {statusMeta?.[toStatus]?.label || toStatus}
          </span>
        </div>

        {/* Preset chips */}
        {presets.length > 0 && (
          <div className="space-y-1.5" data-testid="status-change-presets">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Quick reasons</div>
            <div className="flex flex-wrap gap-1">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReason(p)}
                  className={`text-[11px] px-2 py-1 border transition-colors ${reason === p ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`status-change-preset-${p.slice(0, 20).replace(/\s+/g, '-')}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reason textarea */}
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold" htmlFor="status-change-reason">
            Justification <span className="text-rose-500">*</span>
          </label>
          <Textarea
            id="status-change-reason"
            autoFocus
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain briefly why this transition is happening. Visible in the project activity log."
            className="text-sm rounded-none"
            data-testid="status-change-reason-input"
          />
          <div className="text-[10px] text-muted-foreground">
            Tip: press ⌘/Ctrl+Enter to confirm.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            className="rounded-none"
            data-testid="status-change-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="rounded-none"
            data-testid="status-change-confirm"
          >
            {busy ? "Saving…" : "Confirm change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
